import React, { useState } from "react";
import { Box, Button, Typography, Card, CardContent } from "@mui/material";
import { isMobile } from "../../globalStyles/mobile-design";
import { bulkUploadConfigService } from "../../utils/services/bulk-upload-config.service";
import FileSaver from "file-saver";

const TEMPLATE_FILE_NAMES = {
  product: "product-template.xlsx",
  order: "order-template.xlsx",
  shipment: "shipment-template.xlsx",
  archiveOrder: "historical-template.xlsx",
  parameter_value: "parameter-value-template.xlsx",
  catalog: "catalog-template.xlsx",
  sales_by_category: "sales-by-category-template.xlsx",
  supplier: "supplier-template.xlsx",
  purchase_order: "purchase-order-template.xlsx",
  master_purchase_order: "master-purchase-order-template.xlsx",
  supplier_config: "supplier-config-template.xlsx",
  inventory: "inventory-template.xlsx",
  historical_inventory: "historical-inventory-template.xlsx",
};

const ImageGrid = ({ images, outerSx }) => (
  <Box sx={{ display: "flex", gap: 2, ...outerSx }}>
    {images[0] && (
      <Box
        component="img"
        src={images[0]}
        alt="Image 1"
        sx={{
          width: { xs: "110px", sm: "120px", md: "230px" },
          height: "auto",
          objectFit: "contain",
          borderRadius: "8px",
        }}
      />
    )}
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {images.slice(1).map((img, index) => (
        <Box
          key={index}
          component="img"
          src={img}
          alt={`Image ${index + 2}`}
          sx={{
            width: { xs: "50px", sm: "100px", md: "100px" },
            height: "auto",
            objectFit: "contain",
            borderRadius: "8px",
          }}
        />
      ))}
    </Box>
  </Box>
);

const AddImportCard = ({
  title,
  description,
  showImages = false,
  images = [],
  type,
  onNext,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");

  const fileUploadRef = React.useRef();
  const handleFileUpload = (event) => {
    event.preventDefault();
    fileUploadRef.current.click();
  };

  const uploadFile = async () => {
    try {
      const uploadedFile = fileUploadRef.current.files[0];
      setFileName(uploadedFile);
      setSelectedFile(uploadedFile);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSampleDownload = async () => {
    try {
      const outputFileName = TEMPLATE_FILE_NAMES[type];
      if (!outputFileName) throw new Error("Invalid type for sample download");

      const response = await bulkUploadConfigService.downloadSample(type);
      if (!response?.base64) throw new Error("Invalid file response");

      const sliceSize = 1024;
      const byteCharacters = atob(response.base64);
      const byteArrays = [];
      for (
        let offset = 0;
        offset < byteCharacters.length;
        offset += sliceSize
      ) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
      }

      FileSaver.saveAs(
        new Blob(byteArrays, { type: "application/vnd.ms-excel" }),
        outputFileName,
      );
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  return (
    <>
      <Card
        sx={{
          border: "1px solid #D9D9D9",
          backgroundColor: "var(--bg-paper)",
          padding: 1,
          borderRadius: "8px",
          minHeight: 200,
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            {showImages && isMobile() && (
              <ImageGrid
                images={images}
                outerSx={{
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                }}
              />
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "poppins",
                  fontWeight: 500,
                  fontSize: { sm: "18px", md: "24px", xs: "15px" },
                  color: "var(--text-tertiary)",
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "poppins",
                  fontWeight: 400,
                  fontSize: { sm: "14px", md: "16px", xs: "12px" },
                  color: "var(--text-tertiary)",
                }}
              >
                {description}
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mt={5}
              >
                <Box sx={{ mt: 2 }}>
                  <input
                    onChange={uploadFile}
                    type="file"
                    id="excelFile"
                    accept=".xls, .xlsx, .xlsm, .xlsb, .ods, .csv"
                    ref={fileUploadRef}
                    hidden
                  />
                  <Box>
                    <Button
                      style={{
                        backgroundColor: "var(--bg-default)",
                        marginTop: 5,
                        justifyContent: "flex-start",
                        color: "#000000",
                        textTransform: "none",
                        color: "var(--text-primary)",
                        border: "1px solid var(--border-color)",
                      }}
                      onClick={handleFileUpload}
                    >
                      <Typography>
                        {fileName?.name || "Choose File for import"}
                      </Typography>
                    </Button>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                    }}
                  >
                    <Button
                      variant="contained"
                      style={{
                        color: "var(--text-primary)",
                        textTransform: "none",
                        backgroundColor: "var(--bg-default)",
                        border: "1px solid var(--border-color)",
                        gap: 5,
                        // padding: 5,
                      }}
                      onClick={handleSampleDownload}
                    >
                      <img
                        src="/excel-file.png"
                        width={20}
                        height={15}
                        alt="Sample Excel"
                      />
                      <Typography fontSize={14} fontWeight={400}>
                        Download Template
                      </Typography>
                    </Button>
                  </div>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 2, mt: isMobile() ? 1 : 3 }}>
                <Button
                  disabled={!selectedFile}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#DC585C",
                  }}
                  onClick={() => onNext(selectedFile)}
                >
                  <Typography
                    sx={{
                      fontFamily: "poppins",
                      fontWeight: 500,
                      fontSize: { sm: "12px", md: "14px", xs: "12px" },
                      color: "#ffff",
                    }}
                  >
                    Next
                  </Typography>
                </Button>
              </Box>
            </Box>
            {showImages && !isMobile() && (
              <ImageGrid images={images} outerSx={{ flexWrap: "wrap" }} />
            )}
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default AddImportCard;
